import { Fragment } from 'react'
import { Trans, useTranslation } from 'next-i18next'

import ContentWrapper from '~/tutor-on-demand/components/Content/ContentWrapper'
import HeaderTitle from '~/tutor-on-demand/components/Header/HeaderTitle'
import HeaderWrapper from '~/tutor-on-demand/components/Header/HeaderWrapper'
import List from '~/tutor-on-demand/components/Typography/List'
import TutorOnDemandProductCards from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.component'
import Paragraph from '~/tutor-on-demand/components/Typography/Paragraph'
import ROUTES from '~/shared/constants/routes.const'
import SubContainer from '~/tutor-on-demand/components/Content/SubContainer'
import SubContainerWrapper from '~/tutor-on-demand/components/Content/SubContainerWrapper'
import { SubjectType, getSubjectImage } from '~/components/Metadata/SubjectMetatags'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import BreadCrumbs from '~/encyclopedia/components/BreadCrumbs'

enum Topic {
  ECONOMICAL_ANALYSIS = 'economical-analysis',
  BEAR_MARKETS = 'bear-markets',
  BULLMARKETS = 'bullmarkets',
  INTEREST = 'interest',
  BUSINESS_CYCLES = 'business-cycles',
  COMPARATIVE_ADVANTAGES = 'comparative-advantages',
  DEFLATION = 'deflation',
  PRICE_ELASTICITY_OF_DEMAND = 'price-elasticity-of-demand',
  FINANCIAL_MARKETS = 'financial-markets',
  ECONOMIC_POLICIES = 'economic-policies',
  GROSS_DOMESTIC_PRODUCT = 'gross-domestic-product',
  GROWTH_RATE = 'growth-rate',
  INFLATION = 'inflation',
  KEYNESIAN_ECONOMICS = 'keynesian-economics',
  MACROECONOMICS = 'macroeconomics',
  MICROECONOMICS = 'microeconomics',
  OLIGOPOLY = 'oligopoly',
  OPPORTUNITY_COST = 'opportunity-cost',
  STAGFLATION = 'stagflation',
  TRADE_BARRIERS = 'trade-barriers',
}

export default function TutorOnDemandEconomy({ type }: { type: SubjectType }): React.ReactElement {
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.XL })
  const { t } = useTranslation(['tutorOnDemand', 'tutorOnDemandEconomy'])
  const breadcrumbs = [
    { path: ROUTES.TUTOR_ON_DEMAND_INFORMATION, h1: t('tutorOnDemand:landingPage.whatIsTutorOnDemand.title') },
    { path: ROUTES.TUTOR_ON_DEMAND_ECONOMY, h1: t('tutorOnDemandEconomy:title') },
  ]

  return (
    <Fragment>
      <HeaderWrapper>
        <BreadCrumbs
          breadcrumbs={breadcrumbs}
          fontSize="medium"
          isWhite
          itemClassNames="font-medium items-center"
          wrapperClassNames="mt-1 pr-12 lg:pr-0"
        />

        <HeaderTitle
          iconAlt={getSubjectImage(type, t).alt as string}
          iconSrc={getSubjectImage(type, t).srcKey as string}
        >
          {t('tutorOnDemandEconomy:title')}
        </HeaderTitle>
      </HeaderWrapper>
      <ContentWrapper className="space-y-8">
        <div className="xl:space-x-8 xl:flex">
          <section className="mb-6 space-y-6 xl:mb-0">
            <Paragraph>{t('tutorOnDemandEconomy:contentParagraph1')}</Paragraph>
            <Paragraph>{t('tutorOnDemandEconomy:contentParagraph2')}</Paragraph>
            <Paragraph>{t('tutorOnDemandEconomy:contentParagraph3')}</Paragraph>
            <Paragraph>
              {Trans({
                t,
                i18nKey: 'tutorOnDemandEconomy:contentParagraphEncyclopediaHref',
                components: { a: <a className="underline font-semibold" href={ROUTES.ENCYCLOPEDIA} /> },
                children: t('tutorOnDemandEconomy:contentParagraphEncyclopediaHref'),
              })}
            </Paragraph>
            {!isSmallScreen && (
              <TutorOnDemandProductCards
                className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
                colorScheme="white"
                hideChatWithTutor
                hideVideoChatWithTutor
              />
            )}
          </section>
          <SubContainerWrapper>
            <SubContainer className="w-full max-w-sm" header={t('tutorOnDemandEconomy:weCanHelpYouWith.header')}>
              <List
                items={Object.values(Topic).map((item) => t(`tutorOnDemandEconomy:weCanHelpYouWith.items.${item}`))}
              />
            </SubContainer>
          </SubContainerWrapper>
        </div>
        {isSmallScreen && (
          <TutorOnDemandProductCards
            className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
            colorScheme="white"
            hideChatWithTutor
            hideVideoChatWithTutor
          />
        )}
      </ContentWrapper>
    </Fragment>
  )
}
